import React, { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import {
    Alert,
    Box,
    Button,
    FormControlLabel,
    InputAdornment,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import LocaleFormatPicker from 'components/shared/locale-format-picker/locale-format-picker.component';
import { useState, useEffect } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { useMutation } from '@apollo/client';
import { updateUserSystemSettingsTemplate } from 'apollo/templates/user/mutation.graphql';
import { getChildClinicsWithParentTemplate } from 'apollo/templates/user/query.graphql';
import { useQuery } from '@apollo/client';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { Organization } from 'models/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { permissions } from 'utils/permission';
import InfoText from 'components/shared/info-text.component';
import { DEFAULT_LOCALE_FORMAT, LocaleDateFormat, LocaleLanguage, LocaleNumberFormat } from 'models/locale-format';

const SystemSettings: FC = () => {
    const [changesSaved, setChangesSaved] = useState(false);
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();
    const [isShared, setIsShared] = useState(currentUser.isShared);
    const [systemSetting, setSystemSetting] = useState({
        clinic: '',
        autoArchiveOrderDays: 60,
    });
    const [localeFormat, setLocaleFormat] = useState(DEFAULT_LOCALE_FORMAT);
    const { showBoundary } = useDenthubErrorBoundary();
    const [viewSettings, setViewSettings] = useState({ orderModified: true, deliveryDatePassed: true });
    const { t: translate } = useTranslation(['user-settings', 'common']);

    const { data: { childClinicsWithParent } = {}, loading: childClinicLoading } = useQuery(
        getChildClinicsWithParentTemplate,
        {
            notifyOnNetworkStatusChange: true,
            variables: { parentClinicId: currentOrganization?._id, showOnlyDeliveryLocations: true },
            onError(error) {
                showBoundary(error);
            },
        },
    );

    useEffect(() => {
        setSystemSetting({
            ...systemSetting,
            clinic: currentUser?.systemSetting?.clinic._id as string,
            autoArchiveOrderDays: currentUser?.systemSetting?.autoArchiveOrderDays as number,
        });

        setViewSettings({
            ...viewSettings,
            orderModified: currentUser.viewSettings?.orderModified as boolean,
            deliveryDatePassed: currentUser.viewSettings?.deliveryDatePassed as boolean,
        });

        setLocaleFormat({
            language: (currentUser.localeFormat?.language as LocaleLanguage) ?? DEFAULT_LOCALE_FORMAT.language,
            date: (currentUser.localeFormat?.date as LocaleDateFormat) ?? DEFAULT_LOCALE_FORMAT.date,
            number: (currentUser.localeFormat?.number as LocaleNumberFormat) ?? DEFAULT_LOCALE_FORMAT.number,
        });
    }, [currentUser]);

    const handleViewSettings = (event: React.ChangeEvent<HTMLInputElement>) => {
        setViewSettings({
            ...viewSettings,
            [event.target.name]: event.target.checked,
        });
    };

    const handleSharedStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsShared(event.target.checked);
    };

    const handleClinicChange = (event: SelectChangeEvent<string>) => {
        setSystemSetting({
            ...systemSetting,
            clinic: event.target.value,
        });
    };

    const handleDaysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSystemSetting({
            ...systemSetting,
            autoArchiveOrderDays: Number(event.target.value),
        });
    };

    const [updateUserSystemSettings, { error }] = useMutation(updateUserSystemSettingsTemplate, {
        onCompleted: ({ modifyUserSystemSettings }) => {
            currentUser.systemSetting = modifyUserSystemSettings.systemSetting;
            currentUser.localeFormat = modifyUserSystemSettings.localeFormat;
            currentUser.viewSettings = modifyUserSystemSettings.viewSettings;
            currentUserVar(currentUser);
            setChangesSaved(true);
        },
    });

    const handleSaveChanges = async () => {
        const userSystemSetting = {
            systemSetting: systemSetting,
            localeFormat: localeFormat,
            viewSettings: viewSettings,
            isShared,
        };
        await updateUserSystemSettings({
            variables: { userSystemSettings: userSystemSetting },
        });
    };

    return (
        <Fragment>
            {error ? <AlertSnackbar type="error" message={error?.message} /> : undefined}
            {changesSaved ? <AlertSnackbar type="success" message={translate('common:CHANGES_SAVED')} /> : undefined}
            <PageTitle headingStyle="h2" title={translate('user-settings:SYSTEM_SETTINGS')} />

            <Box mb={3}>
                {currentUser.type === 'dentist' ? (
                    <Box>
                        <Typography variant="h4" sx={{ marginBottom: 1 }}>
                            {translate('user-settings:DEFAULT_CLINIC')}
                        </Typography>
                        {childClinicLoading ? undefined : (
                            <Select fullWidth value={systemSetting.clinic} onChange={handleClinicChange} name="clinic">
                                {childClinicsWithParent?.map((childClinicsWithParent: Organization) => (
                                    <MenuItem key={childClinicsWithParent?._id} value={childClinicsWithParent._id}>
                                        {childClinicsWithParent.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        )}
                    </Box>
                ) : undefined}
            </Box>

            <Box mb={3}>
                <Typography variant="h3" sx={{ marginBottom: 2 }}>
                    {translate('user-settings:LOCALE_FORMAT_HEADING')}
                </Typography>
                <LocaleFormatPicker value={localeFormat} onChange={setLocaleFormat} />
            </Box>

            <Box mb={3}>
                <Typography variant="h3" sx={{ marginBottom: 1 }}>
                    {translate('user-settings:AUTOMATIC_ORDER_ARCHIVING')}
                </Typography>
                <InfoText text={translate('user-settings:AUTOMATIC_ORDER_ARCHIVING_INFO')} />
                <TextField
                    value={systemSetting.autoArchiveOrderDays}
                    onChange={handleDaysChange}
                    name="autoArchiveOrderDays"
                    sx={{ marginTop: 1, width: '100px' }}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">{translate('user-settings:DAYS')}</InputAdornment>,
                    }}
                />
            </Box>

            <Box mb={3}>
                <Typography variant="h3" sx={{ marginBottom: 1 }}>
                    {translate('user-settings:VIEW_SETTINGS')}
                </Typography>
                <Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={viewSettings.orderModified}
                                onChange={handleViewSettings}
                                name="orderModified"
                            />
                        }
                        label={translate('user-settings:INDICATE_IN_RED_WHEN_THE_ORDER_HAS_BEEN_MODIFIED')}
                    />
                </Box>
                <Box>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={viewSettings.deliveryDatePassed}
                                onChange={handleViewSettings}
                                name="deliveryDatePassed"
                            />
                        }
                        label={translate('user-settings:INDICATE_IN_RED_WHEN_THE_DELIVERY_DATE_HAS_PASSED')}
                    />
                </Box>
            </Box>

            {permissions.isLabTechnician(currentUser.type) && currentUser.linkedOrganizations?.length === 1 ? (
                <Box mb={3}>
                    <Typography variant="h4" sx={{ marginBottom: 1 }}>
                        {translate('user-settings:SHARED_USER')}
                    </Typography>
                    <Alert severity="info">{translate('user-settings:SHARED_USER_INFO')}</Alert>
                    <Box>
                        <FormControlLabel
                            control={<Switch checked={isShared} onChange={handleSharedStatusChange} name="isShared" />}
                            label={translate('user-settings:SHARED_USER')}
                        />
                    </Box>
                </Box>
            ) : undefined}

            <Box display="flex" justifyContent="center">
                <Button onClick={handleSaveChanges} variant="contained">
                    {translate('user-settings:SAVE_CHANGES')}
                </Button>
            </Box>
        </Fragment>
    );
};

export default SystemSettings;
